import React, { useState } from 'react';
import { Trash2, Plus, X, ChevronDown, ChevronUp, Check } from 'lucide-react';

function QuestionEditor({ question, questionNumber, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState({ ...question });
  const [hasChanges, setHasChanges] = useState(false);

  const handleChange = (field, value) => {
    setEditedQuestion({ ...editedQuestion, [field]: value });
    setHasChanges(true);
  };

  const handleOptionChange = (index, text) => {
    const newOptions = [...editedQuestion.Options];
    newOptions[index] = { ...newOptions[index], text };
    handleChange('Options', newOptions);
  };

  const handleCorrectOptionChange = (index) => {
    const newOptions = editedQuestion.Options.map((opt, i) => ({
      ...opt,
      isCorrect: i === index
    }));
    handleChange('Options', newOptions);
  };

  const handleAddOption = () => {
    const newOptions = [
      ...editedQuestion.Options,
      { text: `Option ${String.fromCharCode(65 + editedQuestion.Options.length)}`, isCorrect: false }
    ];
    handleChange('Options', newOptions);
  };

  const handleRemoveOption = (index) => {
    if (editedQuestion.Options.length <= 2) {
      alert('A question must have at least 2 options');
      return;
    }
    const newOptions = editedQuestion.Options.filter((_, i) => i !== index);
    handleChange('Options', newOptions);
  };

  const handleTypeChange = (newType) => {
    const updated = { ...editedQuestion, type: newType };

    if (newType === 'MULTIPLE_CHOICE' && !editedQuestion.Options?.length) {
      updated.Options = [
        { text: 'Option A', isCorrect: false },
        { text: 'Option B', isCorrect: false },
        { text: 'Option C', isCorrect: false },
        { text: 'Option D', isCorrect: false }
      ];
    } else if (newType === 'TRUE_FALSE') {
      updated.correctAnswer = 'true';
    } else if (newType === 'SHORT_ANSWER') {
      updated.correctAnswer = '';
    }

    setEditedQuestion(updated);
    setHasChanges(true);
  };

  const handleSave = () => {
    // Prepare data for API
    const dataToSave = {
      type: editedQuestion.type,
      question: editedQuestion.question,
      explanation: editedQuestion.explanation,
      points: editedQuestion.points,
      order: editedQuestion.order
    };

    if (editedQuestion.type === 'MULTIPLE_CHOICE') {
      dataToSave.options = editedQuestion.Options.map((opt, idx) => ({
        text: opt.text,
        isCorrect: opt.isCorrect,
        order: idx
      }));
    } else {
      dataToSave.correctAnswer = editedQuestion.correctAnswer;
    }

    onUpdate(question.id, dataToSave);
    setHasChanges(false);
  };

  return (
    <div className="question-card">
      <div className="question-header" onClick={() => setExpanded(!expanded)}>
        <div className="question-header-left">
          <span className="question-number">Q{questionNumber}</span>
          <span className="question-type-badge">{editedQuestion.type.replace('_', ' ')}</span>
          <span className="question-preview">
            {editedQuestion.question || 'Untitled Question'}
          </span>
        </div>
        <div className="question-header-right">
          <span className="question-points">{editedQuestion.points} {editedQuestion.points === 1 ? 'point' : 'points'}</span>
          <button className="btn-icon" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="question-body">
          {/* Question Type Selector */}
          <div className="form-group">
            <label>Question Type</label>
            <select
              value={editedQuestion.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="form-select"
            >
              <option value="MULTIPLE_CHOICE">Multiple Choice</option>
              <option value="TRUE_FALSE">True/False</option>
              <option value="SHORT_ANSWER">Short Answer</option>
            </select>
          </div>

          {/* Question Text */}
          <div className="form-group">
            <label>Question *</label>
            <textarea
              value={editedQuestion.question}
              onChange={(e) => handleChange('question', e.target.value)}
              placeholder="Enter your question here..."
              className="form-textarea"
              rows="3"
            />
          </div>

          {/* Multiple Choice Options */}
          {editedQuestion.type === 'MULTIPLE_CHOICE' && (
            <div className="form-group">
              <label>Answer Options</label>
              <div className="options-list">
                {editedQuestion.Options?.map((option, index) => (
                  <div key={index} className="option-item">
                    <div className="option-radio">
                      <input
                        type="radio"
                        name={`correct-${question.id}`}
                        checked={option.isCorrect}
                        onChange={() => handleCorrectOptionChange(index)}
                      />
                    </div>
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      className="option-input"
                      placeholder={`Option ${String.fromCharCode(65 + index)}`}
                    />
                    {editedQuestion.Options.length > 2 && (
                      <button
                        className="btn-remove-option"
                        onClick={() => handleRemoveOption(index)}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button className="btn-add-option" onClick={handleAddOption}>
                <Plus className="w-4 h-4" />
                <span>Add Option</span>
              </button>
            </div>
          )}

          {/* True/False Options */}
          {editedQuestion.type === 'TRUE_FALSE' && (
            <div className="form-group">
              <label>Correct Answer</label>
              <div className="true-false-options">
                <label className="radio-label">
                  <input
                    type="radio"
                    value="true"
                    checked={editedQuestion.correctAnswer === 'true'}
                    onChange={(e) => handleChange('correctAnswer', e.target.value)}
                  />
                  <span>True</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    value="false"
                    checked={editedQuestion.correctAnswer === 'false'}
                    onChange={(e) => handleChange('correctAnswer', e.target.value)}
                  />
                  <span>False</span>
                </label>
              </div>
            </div>
          )}

          {/* Short Answer */}
          {editedQuestion.type === 'SHORT_ANSWER' && (
            <div className="form-group">
              <label>Correct Answer</label>
              <input
                type="text"
                value={editedQuestion.correctAnswer || ''}
                onChange={(e) => handleChange('correctAnswer', e.target.value)}
                placeholder="Enter the correct answer"
                className="form-input"
              />
              <small className="form-help">Answer matching is case-insensitive</small>
            </div>
          )}

          {/* Explanation */}
          <div className="form-group">
            <label>Explanation (Optional)</label>
            <textarea
              value={editedQuestion.explanation || ''}
              onChange={(e) => handleChange('explanation', e.target.value)}
              placeholder="Explain the correct answer..."
              className="form-textarea"
              rows="2"
            />
          </div>

          {/* Points */}
          <div className="form-group">
            <label>Points</label>
            <input
              type="number"
              value={editedQuestion.points}
              onChange={(e) => handleChange('points', parseInt(e.target.value) || 1)}
              className="form-input-small"
              min="1"
            />
          </div>

          {/* Actions */}
          <div className="question-actions">
            <button className="btn-delete" onClick={() => onDelete(question.id)}>
              <Trash2 className="w-4 h-4" />
              <span>Delete Question</span>
            </button>
            <button
              className="btn-save-question"
              onClick={handleSave}
              disabled={!hasChanges}
            >
              <Check className="w-4 h-4" />
              <span>Save Changes</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuestionEditor;
